import React from "react";
import { Link, type To } from "react-router-dom";
import { SALES_ORDER_CANCELLED_STATUS_CODE, SalesOrderStatusBadge } from "components/SalesOrderstatus";
import type { InvoiceType } from "components/InvoiceDocument";

export type SalesOrderDetailsPageHeaderProps = {
  onBack: () => void;
  order: any;
  login: string;
  baseRoute?: string;
  quoteModeEnabled: boolean;
  editTo: To;
  editState: object | undefined;
  orderSummarySending: boolean;
  onSendQuoteClick: () => void | Promise<void>;
  onOrderSummaryPreviewClick: () => void;
  onPreviewInvoice: (t: InvoiceType) => void;
  sendInvoiceLoading: boolean;
  onSendInvoiceEmail: () => void | Promise<void>;
  onOrderStatus: (status: number) => void;
  isSignage: boolean;
};

/**
 * Back button, order # / project #, status badges, and Actions dropdown.
 */
export function SalesOrderDetailsPageHeader(props: SalesOrderDetailsPageHeaderProps) {
  const {
    onBack,
    order,
    login,
    baseRoute,
    quoteModeEnabled,
    editTo,
    editState,
    orderSummarySending,
    onSendQuoteClick,
    onOrderSummaryPreviewClick,
    onPreviewInvoice,
    sendInvoiceLoading,
    onSendInvoiceEmail,
    onOrderStatus,
    isSignage,
  } = props;
  const isOrderCancelled = Number(order?.status ?? 0) === SALES_ORDER_CANCELLED_STATUS_CODE;
  const projectViewId = String(
    order?.project_id?._id ||
    order?.project_id?.id ||
    order?.project_id ||
    order?.projectId?._id ||
    order?.projectId?.id ||
    order?.projectId ||
    order?.linked_project_id?._id ||
    order?.linked_project_id?.id ||
    order?.linked_project_id ||
    order?.linked_project?.id ||
    order?.linked_project?._id ||
    ""
  ).trim();

  return (
    <div className="page-header row g-2 align-items-center">
      <div className=" col-lg-7">
        <div className="d-flex align-items-center gap-2 gap-md-3 flex-wrap col-lg-10">
          <button type="button" onClick={onBack} className="btn back-btn surface-btn fw-bold ms-1 ms-md-3">
            <i className="bi bi-arrow-left me-1"></i>
            Back
          </button>

          <h4 className="m-0 order-tittle text-truncate">
            Order <span className="text-dark">#{order?.order_number || "-----"}</span>
          </h4>
          {order?.project_number && (
            <h4 className="m-0 order-tittle text-truncate">
              Project{" "}
              {projectViewId ? (
                <Link
                  to={`${baseRoute}/projects/view/${projectViewId}`}
                  className=" order-link-1"
                >
                  #{order?.project_number || "-----"}
                </Link>
              ) : (
                <span className=" order-link-1">#{order?.project_number || "-----"}</span>
              )}
            </h4>
          )}
        </div>
      </div>

      <div className="col-12 col-lg-5">
        <div className="d-flex align-items-center justify-content-start justify-content-md-end gap-2 flex-wrap">
          <span className="minw-0">
            <SalesOrderStatusBadge
              type="order"
              value={
                order?.status ?? ""
              }
            />
          </span>
          <span className="minw-0">
            <SalesOrderStatusBadge type="payment" value={order?.payment_status ?? ""} />
          </span>

          <div className="dropdown actions-dropdown">
            <button
              className="btn btn-white border rounded-3 px-3 dropdown-toggle no-caret w-100 w-md-auto"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span className="me-1 order-card-cells">Actions</span>
              <i className="bi bi-three-dots"></i>
            </button>
            <ul className="dropdown-menu dropdown-menu-end">
              {login === "signage" ? (
                (order?.status ?? 0) <= 300 && (
                  <li>
                    <Link to={editTo} state={editState} className="dropdown-item text-primary">
                      <i className="bi bi-pencil-fill me-2"></i>Edit
                    </Link>
                  </li>
                )
              ) : (
                (order?.status ?? 0) === 100 && (
                  <li>
                    <Link to={editTo} state={editState} className="dropdown-item text-primary">
                      <i className="bi bi-pencil-fill me-2"></i>Edit
                    </Link>
                  </li>
                )
              )}

              {(baseRoute === "/signage" || baseRoute === "/signageteammember") && quoteModeEnabled && !isOrderCancelled && (
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    disabled={orderSummarySending}
                    onClick={() => void onSendQuoteClick()}
                  >
                    <i className="bi bi-file-earmark-text me-2" />
                    {orderSummarySending ? "Sending…" : "Send Quote"}
                  </button>
                </li>
              )}

              <li>
                <button
                  type="button"
                  className="dropdown-item"
                  onClick={(e) => {
                    e.preventDefault();
                    onOrderSummaryPreviewClick();
                  }}
                >
                  <i className="bi bi-eye me-2" />
                  View/Order Summary
                </button>
              </li>

              {order?.invoice_date && (baseRoute === "/signage" || baseRoute === "/signageteammember") && !isOrderCancelled && (
                <li>
                  <button className="dropdown-item" onClick={() => onPreviewInvoice("sales")}>
                    <i className="bi bi-eye me-2" />
                    View/Download Invoice
                  </button>
                </li>
              )}

              {order?.invoice_date && (baseRoute === "/signage" || baseRoute === "/signageteammember") && (
                <li>
                  <button
                    type="button"
                    className="dropdown-item"
                    disabled={sendInvoiceLoading}
                    onClick={() => void onSendInvoiceEmail()}
                  >
                    <i className="bi bi-send me-2" />
                    {sendInvoiceLoading ? "Sending…" : "Send Invoice"}
                  </button>
                </li>
              )}

              {isSignage && (order?.status ?? 0) <= 300 && (
                <li>
                  <button className="dropdown-item text-danger" onClick={() => onOrderStatus(400)}>
                    <i className="bi bi-x-octagon me-2"></i>Cancel Order
                  </button>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
